import React, { useState, useEffect } from 'react';
import { FiPlus, FiEdit2, FiTrash2, FiSearch, FiCopy, FiTrendingUp } from 'react-icons/fi';
import { getAffiliates, createAffiliate, updateAffiliate, deleteAffiliate, getAffiliateStats } from '../services/affiliates';
import LoadingSpinner from '../components/Common/LoadingSpinner';
import Pagination from '../components/Common/Pagination';
import AffiliateForm from '../components/Affiliates/AffiliateForm';
import toast from 'react-hot-toast';

const statusColors = {
  active: 'bg-green-100 text-green-800',
  inactive: 'bg-gray-100 text-gray-800',
  pending: 'bg-yellow-100 text-yellow-800',
};

export default function Affiliates() {
  const [affiliates, setAffiliates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingAffiliate, setEditingAffiliate] = useState(null);
  const [showStats, setShowStats] = useState(null);
  const [filters, setFilters] = useState({ status: '', search: '', page: 1, limit: 20 });
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    fetchAffiliates();
  }, [filters]);

  const fetchAffiliates = async () => {
    setLoading(true);
    try {
      const data = await getAffiliates(filters);
      setAffiliates(data.data || []);
      setTotalPages(data.pagination?.pages || 1);
    } catch (error) {
      toast.error('Failed to fetch affiliates');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (affiliateData) => {
    try {
      await createAffiliate(affiliateData);
      toast.success('Affiliate created successfully');
      setShowForm(false);
      fetchAffiliates();
    } catch (error) {
      toast.error('Failed to create affiliate');
    }
  };

  const handleUpdate = async (id, affiliateData) => {
    try {
      await updateAffiliate(id, affiliateData);
      toast.success('Affiliate updated successfully');
      setEditingAffiliate(null);
      fetchAffiliates();
    } catch (error) {
      toast.error('Failed to update affiliate');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this affiliate?')) return;
    try {
      await deleteAffiliate(id);
      toast.success('Affiliate deleted successfully');
      fetchAffiliates();
    } catch (error) {
      toast.error('Failed to delete affiliate');
    }
  };

  const handleViewStats = async (affiliate) => {
    try {
      const data = await getAffiliateStats(affiliate._id);
      setShowStats({ affiliate, stats: data.stats });
    } catch (error) {
      toast.error('Failed to fetch stats');
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Affiliate code copied!');
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Affiliates</h1>
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition"
        >
          <FiPlus className="mr-2" /> Add Affiliate
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm text-gray-600 mb-1">Status</label>
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value, page: 1 })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-primary-500"
            >
              <option value="">All</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="pending">Pending</option>
            </select>
          </div>
          
          <div className="md:col-span-2">
            <label className="block text-sm text-gray-600 mb-1">Search</label>
            <div className="relative">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Name or phone number"
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value, page: 1 })}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-primary-500"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Affiliates Table */}
      {loading ? (
        <LoadingSpinner />
      ) : (
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Phone</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Code</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Commission</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Orders</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Earned</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {affiliates.map((affiliate) => (
                  <tr key={affiliate._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <p className="font-medium text-gray-800">{affiliate.name || '-'}</p>
                    </td>
                    <td className="px-6 py-4 text-sm">{affiliate.phone}</td>
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <code className="bg-gray-100 px-2 py-1 rounded text-sm font-mono">{affiliate.code}</code>
                        <button
                          onClick={() => copyCode(affiliate.code)}
                          className="ml-2 text-gray-400 hover:text-gray-600"
                        >
                          <FiCopy className="w-3 h-3" />
                        </button>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm">{affiliate.commission_rate}%</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded-full ${statusColors[affiliate.status]}`}>
                        {affiliate.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm">{affiliate.stats?.orders || 0}</td>
                    <td className="px-6 py-4 text-sm font-medium">${affiliate.stats?.total_commission || 0}</td>
                    <td className="px-6 py-4 text-right">
                      <button
                        onClick={() => handleViewStats(affiliate)}
                        className="text-green-500 hover:text-green-700 mr-3"
                        title="View Stats"
                      >
                        <FiTrendingUp className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setEditingAffiliate(affiliate)}
                        className="text-blue-500 hover:text-blue-700 mr-3"
                      >
                        <FiEdit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(affiliate._id)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <FiTrash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {affiliates.length === 0 && (
            <div className="text-center py-12 text-gray-500">No affiliates found</div>
          )}
          
          {totalPages > 1 && (
            <div className="px-6 py-4 border-t">
              <Pagination
                currentPage={filters.page}
                totalPages={totalPages}
                onPageChange={(page) => setFilters({ ...filters, page })}
              />
            </div>
          )}
        </div>
      )}

      {/* Stats Modal */}
      {showStats && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl w-full max-w-md p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">{showStats.affiliate.name || showStats.affiliate.phone} - Stats</h2>
              <button onClick={() => setShowStats(null)} className="text-gray-500 hover:text-gray-700">
                ✕
              </button>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between py-2 border-b">
                <span className="text-gray-600">Affiliate Code:</span>
                <code className="font-mono">{showStats.affiliate.code}</code>
              </div>
              <div className="flex justify-between py-2 border-b">
                <span className="text-gray-600">Total Clicks:</span>
                <span className="font-medium">{showStats.stats?.clicks || 0}</span>
              </div>
              <div className="flex justify-between py-2 border-b">
                <span className="text-gray-600">Total Orders:</span>
                <span className="font-medium">{showStats.stats?.orders || 0}</span>
              </div>
              <div className="flex justify-between py-2 border-b">
                <span className="text-gray-600">Total Sales:</span>
                <span className="font-medium">${showStats.stats?.total_sales || 0}</span>
              </div>
              <div className="flex justify-between py-2 border-b">
                <span className="text-gray-600">Total Commission:</span>
                <span className="font-medium text-green-600">${showStats.stats?.total_commission || 0}</span>
              </div>
              <div className="flex justify-between py-2">
                <span className="text-gray-600">Paid:</span>
                <span className="font-medium">${showStats.stats?.paid || 0}</span>
              </div>
              <div className="flex justify-between py-2">
                <span className="text-gray-600">Due:</span>
                <span className="font-medium text-orange-600">${showStats.stats?.due || 0}</span>
              </div>
            </div>
            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setShowStats(null)}
                className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Affiliate Form Modal */}
      <AffiliateForm
        isOpen={showForm || !!editingAffiliate}
        onClose={() => {
          setShowForm(false);
          setEditingAffiliate(null);
        }}
        onSubmit={editingAffiliate ? (data) => handleUpdate(editingAffiliate._id, data) : handleCreate}
        initialData={editingAffiliate}
      />
    </div>
  );
}
